/**
 * Login Page Component
 *
 * User authentication interface with:
 * - Google OAuth login (primary)
 * - Email/username and password login (secondary)
 * - Two-factor authentication support
 * - "Remember me" option
 * - Password reset link
 * - Registration link
 */

import React, { useState, useEffect } from 'react';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  LogIn,
  User,
  AlertCircle,
  Loader2,
  Shield,
  ArrowLeft,
  Building2,
} from 'lucide-react';
import { GoogleOAuthProvider, GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { useFormValidation, required, minLength } from '@business-assistant/shared/validation';
import { useTranslation } from '../hooks';

// =============================================================================
// TYPES
// =============================================================================

interface LoginPageProps {
  onLoginSuccess?: (userId: string) => void;
  onRegisterClick?: () => void;
  onForgotPasswordClick?: () => void;
}

type LoginStep = 'credentials' | 'two-factor';

// Get Google Client ID from environment
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

// =============================================================================
// GOOGLE LOGIN WRAPPER
// =============================================================================

interface GoogleLoginButtonProps {
  onSuccess: (response: CredentialResponse) => void;
  onError: () => void;
  disabled?: boolean;
}

function GoogleLoginButton({ onSuccess, onError, disabled }: GoogleLoginButtonProps) {
  if (!GOOGLE_CLIENT_ID) {
    return null;
  }

  return (
    <div className="google-login-wrapper">
      <GoogleLogin
        onSuccess={onSuccess}
        onError={onError}
        useOneTap={false}
        theme="outline"
        size="large"
        width="100%"
        text="signin_with"
        shape="rectangular"
        logo_alignment="left"
      />
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function LoginPage({
  onLoginSuccess,
  onRegisterClick,
  onForgotPasswordClick,
}: LoginPageProps) {
  const auth = useAuth();
  const { t } = useTranslation();
  const [step, setStep] = useState<LoginStep>('credentials');
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form validation hook
  const { errors: validationErrors, validate, touched, setTouched, validateField } = useFormValidation({
    emailOrUsername: [required(t('auth.emailRequired'))],
    password: [required(t('auth.passwordRequired')), minLength(8, t('auth.passwordMinLength'))],
    twoFactorCode: [required(t('auth.codeRequired')), minLength(6, t('auth.codeMinLength'))],
  });

  // Handle credentials submission
  // NOTE: Don't use validate({...}) because it validates ALL schema fields,
  // including twoFactorCode which is not present in the first step and would fail.
  // Use validateField() to validate only the fields needed for each step.
  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    // console.log('[LoginPage] handleCredentialsSubmit called');
    e.preventDefault();
    setError(null);

    // console.log('[LoginPage] Form values:', { emailOrUsername, password: '***', rememberMe });

    // Validate form - only validate credentials fields (not twoFactorCode)
    // IMPORTANT: use validateField() instead of validate() - see note above
    const emailValid = validateField('emailOrUsername', emailOrUsername);
    const passwordValid = validateField('password', password);
    const isFormValid = emailValid && passwordValid;
    // console.log('[LoginPage] Validation result:', { emailValid, passwordValid, isFormValid });
    if (!isFormValid) {
      // console.log('[LoginPage] Validation failed, errors:', validationErrors);
      return;
    }

    // console.log('[LoginPage] Calling auth.login...');
    setIsLoading(true);
    const result = await auth.login(emailOrUsername, password, rememberMe);
    // console.log('[LoginPage] auth.login result:', result);
    setIsLoading(false);

    if (!result.success) {
      // console.log('[LoginPage] Login failed:', result.error);
      setError(result.error || t('auth.genericError'));
      return;
    }

    if (result.requiresTwoFactor) {
      // console.log('[LoginPage] 2FA required');
      setStep('two-factor');
    } else {
      // console.log('[LoginPage] Login successful, calling onLoginSuccess');
      // Login successful
      onLoginSuccess?.('success');
    }
  };

  // Handle Google OAuth success
  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    setError(null);
    setIsGoogleLoading(true);

    if (!credentialResponse.credential) {
      setError(t('auth.googleCredentialsMissing'));
      setIsGoogleLoading(false);
      return;
    }

    const result = await auth.loginWithGoogle({
      idToken: credentialResponse.credential,
    });

    setIsGoogleLoading(false);

    if (!result.success) {
      setError(result.error || t('auth.googleError'));
      return;
    }

    // Login successful
    onLoginSuccess?.('success');
  };

  // Handle Google OAuth error
  const handleGoogleError = () => {
    setError(t('auth.googleFailed'));
    setIsGoogleLoading(false);
  };

  // Handle 2FA submission
  const handleTwoFactorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate 2FA code using new validation hook
    const isFormValid = validate({ twoFactorCode });

    if (!isFormValid) {
      return; // Stop if validation fails
    }

    setIsLoading(true);

    const result = await auth.verify2FA(twoFactorCode);
    setIsLoading(false);

    if (!result.success) {
      setError(result.error || t('auth.invalidCode'));
      return;
    }

    // Login successful
    onLoginSuccess?.('success');
  };

  // Handle back to credentials
  const handleBackToCredentials = () => {
    setStep('credentials');
    setTwoFactorCode('');
    setError(null);
  };

  // Google Icon SVG component
  const GoogleIcon = () => (
    <svg viewBox="0 0 24 24" width="20" height="20" style={{ marginRight: '8px' }}>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );

  const content = (
    <div className="auth-page">
      <div className="auth-container">
        {/* Logo/Branding */}
        <div className="auth-header">
          <div className="auth-logo">
            <Building2 size={40} />
          </div>
          <h1>Business Assistant</h1>
          <p className="auth-subtitle">
            {step === 'credentials'
              ? t('auth.loginToAccount')
              : t('auth.verifyIdentity')}
          </p>
        </div>

        {/* Credentials Step */}
        {step === 'credentials' && (
          <>
            {/* Google OAuth - Primary Login */}
            {GOOGLE_CLIENT_ID && (
              <div className="auth-google-section">
                <GoogleLoginButton
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  disabled={isLoading || isGoogleLoading}
                />
                {isGoogleLoading && (
                  <div className="auth-google-loading">
                    <Loader2 size={18} className="spinner" />
                    <span>{t('auth.googleLoading')}</span>
                  </div>
                )}
              </div>
            )}

            {/* Divider */}
            {GOOGLE_CLIENT_ID && (
              <div className="auth-divider">
                <span>{t('auth.orLoginWithEmail')}</span>
              </div>
            )}

            {/* Email/Password Form - Secondary Login */}
            <form className="auth-form" onSubmit={handleCredentialsSubmit}>
              {/* Email/Username */}
              <div className="auth-field">
                <label className="auth-label">
                  <User size={16} />
                  {t('auth.emailOrUsername')}
                </label>
                <input
                  type="text"
                  className={`auth-input ${touched.emailOrUsername && validationErrors.emailOrUsername ? 'error' : ''}`}
                  placeholder="mario.rossi@example.com"
                  value={emailOrUsername}
                  onChange={(e) => {
                    setEmailOrUsername(e.target.value);
                    if (touched.emailOrUsername) {
                      validateField('emailOrUsername', e.target.value);
                    }
                  }}
                  onBlur={() => {
                    setTouched('emailOrUsername');
                    validateField('emailOrUsername', emailOrUsername);
                  }}
                  required
                  autoFocus={!GOOGLE_CLIENT_ID}
                  disabled={isGoogleLoading}
                />
                {touched.emailOrUsername && validationErrors.emailOrUsername && (
                  <div className="auth-error" style={{ marginTop: '0.25rem', fontSize: '0.75rem' }}>
                    <AlertCircle size={12} style={{ marginRight: '0.25rem' }} />
                    {validationErrors.emailOrUsername}
                  </div>
                )}
              </div>

              {/* Password */}
              <div className="auth-field">
                <label className="auth-label">
                  <Lock size={16} />
                  Password
                </label>
                <div className="auth-input-with-icon">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className={`auth-input ${touched.password && validationErrors.password ? 'error' : ''}`}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (touched.password) {
                        validateField('password', e.target.value);
                      }
                    }}
                    onBlur={() => {
                      setTouched('password');
                      validateField('password', password);
                    }}
                    required
                    disabled={isGoogleLoading}
                  />
                  <button
                    type="button"
                    className="auth-input-icon-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {touched.password && validationErrors.password && (
                  <div className="auth-error" style={{ marginTop: '0.25rem', fontSize: '0.75rem' }}>
                    <AlertCircle size={12} style={{ marginRight: '0.25rem' }} />
                    {validationErrors.password}
                  </div>
                )}
              </div>

              {/* Remember Me */}
              <div className="auth-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={isGoogleLoading}
                  />
                  <span>{t('auth.rememberMe')}</span>
                </label>
                <button
                  type="button"
                  className="auth-link"
                  onClick={onForgotPasswordClick}
                >
                  {t('auth.forgotPassword')}
                </button>
              </div>

              {/* Error */}
              {error && (
                <div className="auth-error">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                className="auth-button auth-button-secondary"
                disabled={isLoading || isGoogleLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className="spinner" />
                    {t('auth.loggingIn')}
                  </>
                ) : (
                  <>
                    <LogIn size={18} />
                    {t('auth.loginWithEmail')}
                  </>
                )}
              </button>
            </form>
          </>
        )}

        {/* Two-Factor Step */}
        {step === 'two-factor' && (
          <form className="auth-form" onSubmit={handleTwoFactorSubmit}>
            <div className="auth-2fa-header">
              <Shield size={48} />
              <p>{t('auth.enter2faCode')}</p>
            </div>

            <div className="auth-field">
              <label className="auth-label">{t('auth.verificationCode')}</label>
              <input
                type="text"
                className={`auth-input auth-input-code ${touched.twoFactorCode && validationErrors.twoFactorCode ? 'error' : ''}`}
                placeholder="000000"
                value={twoFactorCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setTwoFactorCode(value);
                  if (touched.twoFactorCode) {
                    validateField('twoFactorCode', value);
                  }
                }}
                onBlur={() => {
                  setTouched('twoFactorCode');
                  validateField('twoFactorCode', twoFactorCode);
                }}
                required
                autoFocus
                maxLength={6}
              />
              {touched.twoFactorCode && validationErrors.twoFactorCode && (
                <div className="auth-error" style={{ marginTop: '0.25rem', fontSize: '0.75rem' }}>
                  <AlertCircle size={12} style={{ marginRight: '0.25rem' }} />
                  {validationErrors.twoFactorCode}
                </div>
              )}
            </div>

            {/* Error */}
            {error && (
              <div className="auth-error">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              className="auth-button"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="spinner" />
                  {t('auth.verifying')}
                </>
              ) : (
                <>
                  <Shield size={18} />
                  {t('auth.verify')}
                </>
              )}
            </button>

            {/* Back */}
            <button
              type="button"
              className="auth-link-button"
              onClick={handleBackToCredentials}
            >
              <ArrowLeft size={16} />
              {t('auth.goBack')}
            </button>
          </form>
        )}

        {/* Register Link */}
        {step === 'credentials' && (
          <div className="auth-footer">
            <span>{t('auth.noAccount')}</span>
            <button className="auth-link" onClick={onRegisterClick}>
              {t('auth.register')}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // Wrap with GoogleOAuthProvider if client ID is available
  if (GOOGLE_CLIENT_ID) {
    return (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        {content}
      </GoogleOAuthProvider>
    );
  }

  return content;
}
